import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Stack, Tooltip, Typography } from "@mui/material";
import useAuthStore from "../../stores/useAuthStore";
import { useEffect, useState } from "react";
import summary from "../../api/auth/reservations/summary";
import WushCard from "../../components/layout/WushCard";
import PageLoading from "../../components/layout/PageLoading";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { addDays, format, subDays } from "date-fns";
import WushTextField from "../../components/form/WushTextField";
import create from "../../api/auth/orders/create";
import useOrderStore from "../../stores/useOrderStore";
import toCurrency from "../../services/toCurrency";
import getGrossSalesByDay from "../../api/auth/summaries/getGrossSalesByDay";
import getEarnedRevenueByDay from "../../api/auth/summaries/getEarnedRevenueByDay";
import getReservationRevenueByDay from "../../api/auth/summaries/getReservationRevenueByDay";

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addOrder } = useOrderStore();

  const defaultStartAt = searchParams.get('startAt') ? addDays(new Date(searchParams.get('startAt')), 1) : subDays(new Date(), 1);
  const defaultEndAt = searchParams.get('endAt') ? addDays(new Date(searchParams.get('endAt')), 1) : addDays(defaultStartAt, 7);

  const [startAt, setStartAt] = useState(format(defaultStartAt, 'yyyy-MM-dd'));
  const [endAt, setEndAt] = useState(format(defaultEndAt, 'yyyy-MM-dd'));
  const [grossSales, setGrossSales] = useState();
  const [earnedRevenue, setEarnedRevenue] = useState();
  const [reservationRevenue, setReservationRevenue] = useState();

  const handleSetStartAt = (e) => {
    setStartAt(e.target.value);
    setSearchParams({ startAt: e.target.value, endAt: endAt });
  };

  const handleSetEndAt = (e) => {
    setEndAt(e.target.value);
    setSearchParams({ startAt: startAt, endAt: e.target.value });
  };

  const { organization } = useAuthStore();

  const [summaryData, setSummaryData] = useState();

  const navigate = useNavigate();

  useEffect(() => {
    getGrossSalesByDay({
      startDate: startAt,
      endDate: endAt,
    }, (response) => {
      setGrossSales(response.total);
    });
  }, [startAt, endAt]);

  useEffect(() => {
    getEarnedRevenueByDay({
      startDate: startAt,
      endDate: endAt,
    }, (response) => {
      setEarnedRevenue(response.total);
    });
  }, [startAt, endAt]);

  useEffect(() => {
    getReservationRevenueByDay({
      startDate: startAt,
      endDate: endAt,
    }, (response) => {
      setReservationRevenue(response.total);
    });
  }, [startAt, endAt]);

  useEffect(() => {
    summary({
      status: ['confirmed', 'paid'],
      startAt: startAt,
      endAt: endAt,
    }, (response) => {
      setSummaryData(response);
    });
  }, [startAt, endAt]);

  const backgroundColor = (count) => {
    if(count === 0) {
      return "lightgreen";
    } else if(count < 5) {
      return "lightyellow";
    } else {
      return "lightcoral";
    }
  };

  const handleCreateOrder = () => {
    create({ test: 'value' }, (response) => {
      addOrder(response);
      navigate(`/orders/${response.id}`);
    });
  };

  if(!summaryData) return <PageLoading />;

  return (
    <Stack>
      <Stack mb={2}>
        <Typography variant="h4"><b>{organization.name}</b> Mission Control</Typography>
      </Stack>
      <Stack direction="row" mb={2}>
        <Button onClick={handleCreateOrder} variant="contained" color="primary">Create Order</Button>
      </Stack>
      <Stack width={{ xs: "100%", sm: "50%" }} mb={3} >
        <WushCard>
          <Stack p={2} spacing={2}>
            <Stack direction="row" spacing={2}>
              <WushTextField type="date" label="Start Date" value={startAt} onChange={handleSetStartAt} />
              <WushTextField type="date" label="End Date" value={endAt} onChange={handleSetEndAt} />
              <Button variant="contained" color="secondary" onClick={() => {
                setStartAt(format(new Date(), 'yyyy-MM-dd'));
                setEndAt(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
                setSearchParams({});
              }}>
                Reset
              </Button>
            </Stack>
            <Stack>
              <Accordion>
                <AccordionSummary>
                  <Typography variant="h6">Revenue Summary</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container mb={2} spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Tooltip title="This is your gross sales during this time period">
                        <Typography variant="body1" fontWeight="bold">Gross Sales</Typography>
                      </Tooltip>
                      <Typography variant="body1">{toCurrency(grossSales / 100)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Tooltip title="This is your revenue from orders that were paid for during this time period">
                        <Typography variant="body1" fontWeight="bold">Earned Revenue</Typography>
                      </Tooltip>
                      <Typography variant="body1">{toCurrency(earnedRevenue / 100)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Tooltip title="This is your revenue from reservations that took place during this time period">
                        <Typography variant="body1" fontWeight="bold">Reservation Revenue</Typography>
                      </Tooltip>
                      <Typography variant="body1">{toCurrency(reservationRevenue / 100)}</Typography>
                    </Grid>
                  </Grid>
                  <Stack>
                    <Link to={`/revenue?startAt=${startAt}&endAt=${endAt}`}>
                      <Typography variant="body1">View more revenue details</Typography>
                    </Link>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Stack>
        </WushCard>
      </Stack>
      <Stack>
        <Grid container gap={2} mb={4}>
          <Grid item xs={12}>
            <Stack spacing={2} direction="row">
              <Typography variant="h5" fontWeight="bold">Reservations awaiting your action</Typography>
              <Button onClick={() => navigate(`/reservations?startAt=${startAt}&endAt=${endAt}`)}>View all</Button>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <WushCard
              bgcolor={backgroundColor(summaryData.upcoming_deliveries)}
              onClick={() => navigate(`/deliveries?startAt=${startAt}&endAt=${endAt}`)}
            >
              <Stack p={2} direction="row" spacing={2}>
                <Stack my="auto" border="1px solid black" borderRadius="100px" width="25px" height="25px" textAlign="center" justifyContent="center">
                  <Typography sx={{ my: "auto" }} variant="h6" fontWeight="bold">{summaryData.upcoming_deliveries}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="h5" fontWeight="bold">Deliveries</Typography>
                  <Typography variant="body1">Reservations that need to be delivered</Typography>
                </Stack>
              </Stack>
            </WushCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <WushCard
              bgcolor={backgroundColor(summaryData.upcoming_pickups)}
              onClick={() => navigate(`/pickups?startAt=${startAt}&endAt=${endAt}`)}
            >
              <Stack p={2} direction="row" spacing={2}>
                <Stack my="auto" border="1px solid black" borderRadius="100px" width="25px" height="25px" textAlign="center" justifyContent="center">
                  <Typography sx={{ my: "auto" }} variant="h6" fontWeight="bold">{summaryData.upcoming_pickups}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="h5" fontWeight="bold">Pickups</Typography>
                  <Typography variant="body1">Reservations that need to be picked up</Typography>
                </Stack>
              </Stack>
            </WushCard>
          </Grid>
        </Grid>
        <Grid container gap={2}>
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="bold">Reservations awaiting customer action</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <WushCard
              bgcolor={backgroundColor(summaryData.upcoming_customer_pickups)}
              onClick={() => navigate(`/customer_pickups?startAt=${startAt}&endAt=${endAt}`)}
            >
              <Stack p={2} direction="row" spacing={2}>
                <Stack my="auto" border="1px solid black" borderRadius="100px" width="25px" height="25px" textAlign="center" justifyContent="center">
                  <Typography sx={{ my: "auto" }} variant="h6" fontWeight="bold">{summaryData.upcoming_customer_pickups}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="h5" fontWeight="bold">Customer Pickups</Typography>
                  <Typography variant="body1">Reservations that need to be picked up by the customer</Typography>
                </Stack>
              </Stack>
            </WushCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <WushCard
              bgcolor={backgroundColor(summaryData.upcoming_customer_returns)}
              onClick={() => navigate(`/customer_returns?startAt=${startAt}&endAt=${endAt}`)}
            >
              <Stack p={2} direction="row" spacing={2}>
                <Stack my="auto" border="1px solid black" borderRadius="100px" width="25px" height="25px" textAlign="center" justifyContent="center">
                  <Typography sx={{ my: "auto" }} variant="h6" fontWeight="bold">{summaryData.upcoming_customer_returns}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="h5" fontWeight="bold">Customer Returns</Typography>
                  <Typography variant="body1">Reservations that need to be returned by the customer</Typography>
                </Stack>
              </Stack>
            </WushCard>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
}